/*
 * Copyright (c) 2025-2026 Aegis AO Soft LLC and Alexander Orlov.
 * Confidential and proprietary. Author: Alexander Orlov.
 *
 * Picks localized storefront text for the current UI language. The owner-web Design editor authors
 * translations keyed by the admin GUI's i18n codes (en, es, …, zh-Hans, zh-Hant); the storefront uses
 * zh-CN/zh-TW for Chinese, so those are aliased here. Falls back to English, then any non-empty value,
 * then the legacy single-string base column.
 */

export type LocalizedTextMap = Record<string, string>;

/** Codes to try for a UI language, bridging the storefront ↔ admin Chinese code mismatch. */
export function langAliases(lang: string): string[] {
  switch (lang) {
    case 'zh-CN': return ['zh-CN', 'zh-Hans'];
    case 'zh-TW': return ['zh-TW', 'zh-Hant'];
    case 'zh-Hans': return ['zh-Hans', 'zh-CN'];
    case 'zh-Hant': return ['zh-Hant', 'zh-TW'];
    default: return [lang];
  }
}

/** Pick a value from a localized map for `lang` (with aliases), else English, else first non-empty. */
export function pickFromMap(map: LocalizedTextMap | undefined | null, lang: string): string {
  if (!map) return '';
  for (const k of langAliases(lang)) if (map[k]?.trim()) return map[k];
  if (map.en?.trim()) return map.en;
  for (const k of Object.keys(map)) if (map[k]?.trim()) return map[k];
  return '';
}

/**
 * Pick localized text from a JSON localized-map string, falling back to the legacy single-string
 * `base` column. Used for the Design-tab text fields (tag line, contact address, …).
 */
export function pickLocalized(json: string | null | undefined, base: string | null | undefined, lang: string): string {
  let map: LocalizedTextMap | null = null;
  if (json) {
    try {
      const o = JSON.parse(json);
      if (o && typeof o === 'object' && !Array.isArray(o)) map = o as LocalizedTextMap;
    } catch {
      /* fall back to base */
    }
  }
  const v = map ? pickFromMap(map, lang) : '';
  return v || base || '';
}

/** Localized vehicle-category label for a car: NameJson → base CategoryName/TypeName → fallback. */
export function categoryLabel(
  car: { categoryNameJson?: string | null; categoryName?: string | null; typeName?: string | null },
  lang: string,
  fallback = '',
): string {
  return pickLocalized(car.categoryNameJson, car.categoryName || car.typeName || fallback, lang) || fallback;
}
